import { useEffect, useRef, useState } from 'react';
import { Box, Center, Flex, InputGroup, Spinner, Switch, Text } from '@chakra-ui/react';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  ClipboardIconButton,
  ClipboardInput,
  ClipboardLabel,
  ClipboardRoot,
} from '@/components/ui/clipboard';
import { toaster } from '@/components/ui/toaster';
import { buildPublicUrl } from '@/utils/public-url';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import api from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { Course } from '@/lib/types';

interface ShareCourseDialogProps {
  course: Course;
  isOpen: boolean;
  onClose: () => void;
  // Called with the updated course after the share endpoint stamps shared_at,
  // so the parent view stays truthful. Sharing no longer locks the modes —
  // that happens when a learner first records progress.
  onShared: (course: Course) => void;
}

// Share dialog: opening it is the moment the share becomes real — the share
// endpoint stamps shared_at and permanently locks the tracking mode, then the
// link is offered for copying. The link always opens the latest published
// version and never expires.
export default function ShareCourseDialog({
  course,
  isOpen,
  onClose,
  onShared,
}: ShareCourseDialogProps) {
  const isAdmin = useIsInAdminGroup();
  const [isLoading, setIsLoading] = useState(false);
  const [isTogglingCommunity, setIsTogglingCommunity] = useState(false);
  const sharedRef = useRef(false);

  const toggleCommunity = async (enabled: boolean) => {
    setIsTogglingCommunity(true);
    try {
      const res = await api.put(`courses/${course.id}/community`, { enabled });
      onShared(unwrap<Course>(res));
      toaster.create({
        title: enabled ? 'Listed in the Community Hub' : 'Removed from the Community Hub',
        type: 'success',
      });
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to update the community listing',
        type: 'error',
      });
    } finally {
      setIsTogglingCommunity(false);
    }
  };

  useEffect(() => {
    if (!isOpen || sharedRef.current) return;
    sharedRef.current = true;
    setIsLoading(true);
    api
      .post(`courses/${course.id}/share`)
      .then((res) => onShared(unwrap<Course>(res)))
      .catch((error: any) => {
        toaster.create({
          title: error.response?.data?.error || 'Failed to share course',
          type: 'error',
        });
        sharedRef.current = false;
        onClose();
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const link = buildPublicUrl('courses', course.reference_id);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Share this course</DialogTitle>
        </DialogHeader>
        <DialogBody pb={6}>
          {isLoading ? (
            <Center py={6}>
              <Spinner size='lg' />
            </Center>
          ) : (
            <Box>
              <ClipboardRoot maxW='full' value={link}>
                <ClipboardLabel>Anyone with the link can open the course</ClipboardLabel>
                <InputGroup width='full' endElement={<ClipboardIconButton me='-2' />}>
                  <ClipboardInput />
                </InputGroup>
              </ClipboardRoot>
              <Text fontSize='xs' color='fg.muted' mt={3}>
                {course.access_mode === 'open'
                  ? 'No sign-in needed — progress is counted anonymously.'
                  : 'The link asks recipients to sign in before progress is recorded.'}{' '}
                The link always opens the latest published version. Sharing locks the tracking
                mode for good.
              </Text>

              {isAdmin && (
                <Flex
                  align='center'
                  justify='space-between'
                  gap={4}
                  mt={5}
                  pt={4}
                  borderTopWidth='1px'
                  borderColor='border'
                >
                  <Box>
                    <Text fontSize='sm' fontWeight='600' color='fg.strong'>
                      List in the Community Hub
                    </Text>
                    <Text fontSize='xs' color='fg.muted'>
                      Other Coach Hub organizations can preview this course and copy it into
                      their own workspace. Un-listing keeps copies they already made.
                    </Text>
                  </Box>
                  <Switch.Root
                    size='sm'
                    checked={course.community_shared_at != null}
                    disabled={isTogglingCommunity}
                    onCheckedChange={(e) => toggleCommunity(e.checked)}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                  </Switch.Root>
                </Flex>
              )}
            </Box>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
